import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import SocialAuthButtons from '../components/auth/SocialAuthButtons';
import ConsentCheckboxes, { type ConsentValues } from '../components/auth/ConsentCheckboxes';
import PageSEO from '../components/PageSEO';

interface FormState {
  firstName:   string;
  lastName:    string;
  email:       string;
  phone:       string;
  password:    string;
}

export default function SignupPage() {
  const navigate           = useNavigate();
  const [searchParams]     = useSearchParams();
  const { user, loading }  = useAuth();
  // Read ref from URL param first, then sessionStorage relay (set by WaitlistPage)
  const refCode = searchParams.get('ref')
    ?? sessionStorage.getItem('referral_code')
    ?? null;

  const [form, setForm]       = useState<FormState>({
    firstName: '', lastName: '', email: '', phone: '', password: '',
  });
  const [consent, setConsent] = useState<ConsentValues>({
    consentSms: false, consentTerms: false, marketingOptIn: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [checkEmail,   setCheckEmail]   = useState(false);

  // Already logged in → send to dashboard
  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [user, loading, navigate]);

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  // Basic phone validation — must have at least 10 digits
  const phoneDigits = form.phone.replace(/\D/g, '');
  const phoneValid  = phoneDigits.length >= 10;

  const canSubmit =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.email.trim() &&
    phoneValid &&
    form.password.length >= 8 &&
    consent.consentTerms;  // SMS consent is optional (A2P 30923): never gate signup on it

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);

    try {
      // 1. Create Supabase auth user.
      //    All form fields are stashed in user_metadata so they survive the
      //    email-verification gap. When email confirmation is enabled,
      //    signUp() returns no session and the immediate complete-signup
      //    call below is skipped — AuthCallbackPage reads these values from
      //    user_metadata after verification and finishes the profile then.
      const { data, error: signUpError } = await supabase.auth.signUp({
        email:    form.email.trim().toLowerCase(),
        password: form.password,
        options:  {
          data: {
            first_name:        form.firstName.trim(),
            last_name:         form.lastName.trim(),
            phone:             form.phone.trim(),
            consent_sms:       consent.consentSms,
            consent_terms:     consent.consentTerms,
            marketing_opt_in:  consent.marketingOptIn,
            referred_by_code:  refCode ?? null,
          },
          // Supabase sends a confirmation email if email confirm is ON in dashboard
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (signUpError) {
        setError(signUpError.message);
        return;
      }

      // 2. If email confirmation is DISABLED → session is returned immediately
      //    If ENABLED → session is null (user must confirm email first)
      if (data.session) {
        // Complete the profile with consent data
        await apiFetch('/api/auth/complete-signup', {
          method: 'POST',
          body:   JSON.stringify({
            firstName:      form.firstName.trim(),
            lastName:       form.lastName.trim(),
            phone:          form.phone.trim(),
            consentSms:     consent.consentSms,
            consentTerms:   consent.consentTerms,
            marketingOptIn: consent.marketingOptIn,
            referredByCode: refCode ?? undefined,
          }),
        });
        // Clear relay so the code doesn't fire on a future unrelated signup
        sessionStorage.removeItem('referral_code');
        navigate('/dashboard', { replace: true });
      } else {
        // Email confirmation required — show the "check your email" state
        setCheckEmail(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Check-your-email screen ───────────────────────────────────────────────
  if (checkEmail) {
    return (
      <AuthShell>
        <div className="text-center">
          <div className="text-5xl mb-5">💌</div>
          <h1 className="font-heading text-2xl text-navy mb-3">Check your email</h1>
          <p className="font-body text-gray-500 text-sm leading-relaxed mb-6">
            We sent a confirmation link to{' '}
            <strong className="text-navy">{form.email}</strong>.
            Click it to activate your account.
          </p>
          <p className="font-body text-xs text-gray-400">
            Wrong address?{' '}
            <button
              onClick={() => setCheckEmail(false)}
              className="text-sage hover:underline"
            >
              Go back
            </button>
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <PageSEO
        title="Start Your Free Trial | MomentSent"
        description="Create your MomentSent account and start your free 14-day trial today. No credit card required. Automatic birthday card and greeting card mailing service."
        canonical="https://momentsent.com/signup"
      />
      <div className="text-center mb-7">
        <h1 className="font-heading text-3xl text-navy mb-2">Create your account</h1>
        <p className="font-body text-sm text-gray-500">
          14-day free trial · No credit card required
        </p>
      </div>

      {/* Social auth buttons */}
      <SocialAuthButtons mode="signup" />

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="font-body text-xs text-gray-400">or continue with email</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate className="space-y-3">

        {/* Name row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="firstName" className="sr-only">First name</label>
            <input
              id="firstName"
              type="text"
              placeholder="First name"
              autoComplete="given-name"
              value={form.firstName}
              onChange={set('firstName')}
              required
              disabled={submitting}
              className="input"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="sr-only">Last name</label>
            <input
              id="lastName"
              type="text"
              placeholder="Last name"
              autoComplete="family-name"
              value={form.lastName}
              onChange={set('lastName')}
              required
              disabled={submitting}
              className="input"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="sr-only">Email address</label>
          <input
            id="email"
            type="email"
            placeholder="Email address"
            autoComplete="email"
            value={form.email}
            onChange={set('email')}
            required
            disabled={submitting}
            className="input"
          />
        </div>

        {/* Phone — required for SMS reminders */}
        <div>
          <label htmlFor="phone" className="sr-only">Phone number</label>
          <input
            id="phone"
            type="tel"
            placeholder="(555) 555-5555"
            autoComplete="tel"
            value={form.phone}
            onChange={set('phone')}
            required
            disabled={submitting}
            className="input"
          />
          <p className="mt-1.5 font-body text-xs text-gray-400">
            📱 We'll send you a text 14 days before each card mails so you can review and approve it. Standard rates apply.
          </p>
          {form.phone.trim() && !phoneValid && (
            <p className="mt-1 text-xs text-red-500 font-body">
              Please enter a valid phone number (at least 10 digits).
            </p>
          )}
        </div>

        {/* Password */}
        <div className="relative">
          <label htmlFor="password" className="sr-only">Password</label>
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Password (8+ characters)"
            autoComplete="new-password"
            value={form.password}
            onChange={set('password')}
            required
            minLength={8}
            disabled={submitting}
            className="input pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {/* Consent checkboxes */}
        <div className="pt-1 pb-1">
          <ConsentCheckboxes
            values={consent}
            onChange={setConsent}
            disabled={submitting}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 animate-fade-in">
            <p className="font-body text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={!canSubmit || submitting}
          className="w-full btn-primary flex items-center justify-center gap-2
                     disabled:opacity-50 disabled:cursor-not-allowed
                     disabled:hover:shadow-none disabled:hover:translate-y-0"
        >
          {submitting ? (
            <><Loader2 size={16} className="animate-spin" /><span>Creating account…</span></>
          ) : (
            <span>Create my account →</span>
          )}
        </button>

      </form>

      {/* Sign in link */}
      <p className="mt-5 text-center font-body text-sm text-gray-500">
        Already have an account?{' '}
        <Link to="/login" className="text-sage font-medium hover:underline">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}

// ── Shared auth page shell ────────────────────────────────────────────────────

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-4">
      {/* Logo / back to home */}
      <Link to="/" className="mb-8 hover:opacity-80 transition-opacity">
        <img
          src="/logo-wordmark.png"
          alt="MomentSent"
          className="h-28 w-auto object-contain"
        />
      </Link>

      {/* Card */}
      <div className="w-full max-w-md bg-white rounded-card shadow-warm-lg p-8">
        {children}
      </div>

      {/* Footer micro-copy */}
      <p className="mt-6 font-body text-xs text-gray-400 text-center max-w-xs">
        © {new Date().getFullYear()} MomentSent LLC, Hubert, NC
      </p>
    </div>
  );
}

export { AuthShell };
